import PsychoeducationBlock from "../_shared/PsychoeducationBlock";
import AllergiesBlock from "../_shared/AllergiesBlock";
import HistoryBlock from "../_shared/HistoryBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import VitalsBlock from "../_shared/VitalsBlock";
import RosBlock from "../_shared/RosBlock";
import PhysicalExamBlock from "../_shared/PhysicalExamBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import AssessmentBlock from "../_shared/AssessmentBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import CptCodesBlock from "../_shared/CptCodesBlock";
import {
  hasAssessmentContent,
  hasCptCodes,
  hasFollowUp,
  hasObjectiveContent,
  hasPlanContent,
  hasPsychoeducationContent,
  hasSubjectiveContent,
} from "../_shared/section-content";
import {
  CarePlanTemplateList,
  OrderList,
  RenderTextWithContainerPattern,
  ReportSection,
} from "../constant";

interface PsychoeducationTherapyTemplateProps {
  data: any;
}

/**
 * The Psychoeducation form captures none of the SOAP free notes or clinical
 * blocks, so every band is gated on having content — otherwise the printed note
 * opens with empty Subjective/Objective/Plan headers.
 */
const PsychoeducationTherapyTemplate = ({
  data,
}: PsychoeducationTherapyTemplateProps) => {
  return (
    <>
      {/* ── Subjective ── */}
      {hasSubjectiveContent(data) && (
        <ReportSection label="Subjective">
          <RenderTextWithContainerPattern
            label="History of Present Illness"
            val={data?.historyOfPresentIllness}
          />
          <RenderTextWithContainerPattern
            label="Subjective"
            val={data?.subjectiveFreeNote}
          />
          <AllergiesBlock data={data} />
          <HistoryBlock data={data} />
          <RenderTextWithContainerPattern label="Habits" val={data?.habits} />
          <MedicationsBlock data={data} />
        </ReportSection>
      )}

      {/* ── Objective ── */}
      {hasObjectiveContent(data) && (
        <ReportSection label="Objective">
          <RenderTextWithContainerPattern
            label="Objective"
            val={data?.objectiveFreeNote}
          />
          <VitalsBlock data={data} />
          <PhysicalExamBlock data={data} />
          <RosBlock data={data} />
          <ScreeningAssessmentBlock data={data} />
        </ReportSection>
      )}

      {/* ── Plan ── CPT codes print under Clinical Diagnoses for this note, so
          they are deliberately not part of this band's content check. */}
      {hasPlanContent(data) && (
        <ReportSection label="Plan">
          <RenderTextWithContainerPattern
            label="Plan"
            val={data?.planFreeNote}
          />
          <PrescriptionsBlock data={data} />
          <VaccinesBlock data={data} />
          <OrderList orders={data?.patientOrders} />
          <RenderTextWithContainerPattern
            label="Care Plan"
            val={data?.carePlan}
          />
          <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
          <RenderTextWithContainerPattern
            label="Instructions"
            val={data?.instruction}
          />
        </ReportSection>
      )}

      {/* ── Follow-Up ── */}
      {hasFollowUp(data) && (
        <ReportSection label="Follow-Up">
          <RenderTextWithContainerPattern
            label="Follow Up"
            val={data?.followUp}
          />
        </ReportSection>
      )}

      {/* ── Psychoeducation Specific ── */}
      {hasPsychoeducationContent(data) && (
        <ReportSection label="Psychoeducation">
          <PsychoeducationBlock data={data} gated={false} />
        </ReportSection>
      )}

      {/* ── Clinical Diagnoses (ICD/CPT) ── closes the note, directly after the
          psychoeducation Provider Notes, mirroring the on-screen form flow. */}
      {(hasAssessmentContent(data) || hasCptCodes(data)) && (
        <ReportSection label="Clinical Diagnoses">
          <AssessmentBlock data={data} />
          <CptCodesBlock data={data} />
        </ReportSection>
      )}
    </>
  );
};

export default PsychoeducationTherapyTemplate;
